/**
 * Join-flow onboarding step registry and route helpers.
 *
 * The flow is: /join/:orgId → /join/:orgId/:stepId (three-step wizard).
 * Steps are identified by stable slugs so the current step survives refresh
 * and browser back/forward.
 */

export interface JoinStep {
  /** Stable slug used in the URL (/join/:orgId/:stepId). */
  id: string;
  /** Label shown in the step sidebar. */
  label: string;
  /** Page title shown above the step content. */
  title: string;
  /** Optional subtitle below the title. */
  subtitle?: string;
}

export const JOIN_STEPS: readonly JoinStep[] = [
  {
    id: 'about-you',
    label: 'Tell us about you',
    title: 'Tell us about you',
  },
  {
    id: 'permissions',
    // Intentional divergence: the sidebar label and the page title are different
    // by design — the label is a short action verb, the title is a question that
    // frames the user's decision. Do not "fix" them to match.
    label: 'Set your permissions',
    title: 'What should Ahoy see?',
    subtitle:
      'Ahoy preps from your email so your follow-ups write themselves. You decide how much it reads. Change this anytime in settings.',
  },
  {
    id: 'learn',
    label: 'Learn about Ahoy',
    title: 'Learn about Ahoy',
  },
];

/**
 * Max length of the free-text "role" answer. Mirrors the backend serializer's
 * `role = serializers.CharField(..., max_length=100)` in
 * apps/orgs/views/join_onboarding_views.py — capping the input up front stops an
 * over-length role reaching the completion POST, where it would be rejected with a
 * 400 the user cannot recover from on the final step.
 */
export const ROLE_MAX_LENGTH = 100;

/**
 * Max length of the first/last name answers. Mirrors the backend serializer's
 * `max_length=150`, which in turn mirrors `CustomUser.first_name`/`last_name`
 * (Django's own 150-char fields) — the same "reject at the input, not at the
 * final POST" reasoning as ROLE_MAX_LENGTH above.
 */
export const NAME_MAX_LENGTH = 150;

export const JOIN_BASE_PATH = '/join';

export function joinBasePath(orgId: string): string {
  return `${JOIN_BASE_PATH}/${orgId}`;
}

export function joinStepPath(orgId: string, stepId: string): string {
  return `${JOIN_BASE_PATH}/${orgId}/${stepId}`;
}

export function dashboardPath(orgId: string): string {
  return `/${orgId}`;
}

export const FIRST_JOIN_STEP_ID = JOIN_STEPS[0].id;

export function findJoinStepIndex(stepId: string | undefined): number {
  if (!stepId) {
    return -1;
  }
  return JOIN_STEPS.findIndex(step => step.id === stepId);
}
